import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import { StatusPill } from '@/components/common/StatusPill';
import { BadgeCheck, Receipt, RefreshCw, Tickets, Users } from '@/components/common/icons';
import { ApprovalActionDialog } from '@/features/approvals/components/ApprovalActionDialog';
import { ApprovalsTabsAndFilters } from '@/features/approvals/components/ApprovalsTabsAndFilters';
import { AttendanceApprovalTable, IntimationApprovalTable, LeaveApprovalTable } from '@/features/approvals/components/GenericApprovalTables';
import { TicketApprovalTable } from '@/features/approvals/components/TicketApprovalTable';
import { useApprovalsData } from '@/features/approvals/useApprovalsData';
import { MyApprovalStatusSection } from '@/features/my-approval-status/components/MyApprovalStatusSection';
import { TicketDetailsDialog } from '@/features/tickets/components/TicketDetailsDialog';
import { fetchTicketDetails } from '@/features/tickets/api';

function ApprovalServerPager({ loading, pageInfo = {}, onPageChange }) {
  const page = Math.max(1, Number(pageInfo.page || 1));
  const pageSize = Math.max(1, Number(pageInfo.pageSize || 10));
  const total = Math.max(0, Number(pageInfo.total || 0));
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize && page <= 1) return null;
  const first = total ? ((page - 1) * pageSize) + 1 : 0;
  const last = Math.min(page * pageSize, total);
  return (
    <div className="react-data-table__footer approval-server-pager">
      <span className="react-data-table__info">Showing {first} to {last} of {total} entries</span>
      <div className="react-data-table__pager">
        <button type="button" disabled={loading || page <= 1} onClick={() => onPageChange(1)} aria-label="First page">First</button>
        <button type="button" disabled={loading || page <= 1} onClick={() => onPageChange(Math.max(1, page - 1))} aria-label="Previous page">Prev</button>
        <span className="react-data-table__pager-current">{page}</span>
        <button type="button" disabled={loading || page >= pageCount} onClick={() => onPageChange(Math.min(pageCount, page + 1))} aria-label="Next page">Next</button>
        <button type="button" disabled={loading || page >= pageCount} onClick={() => onPageChange(pageCount)} aria-label="Last page">Last</button>
      </div>
    </div>
  );
}

export function ApprovalsPage({ defaultTab }) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const userRole = String(user?.Role || user?.role || '').trim().toLowerCase();
  const isTL = /^(team leader|tl)$/i.test(userRole);
  const canApprove = /manager|admin|hr|pc|super admin|company admin|team leader|tl/i.test(userRole);

  const {
    employeeId,
    currentUser,
    activeTab,
    setActiveTab,
    loading,
    rowsLoading,
    pagination,
    setApprovalPage,
    error, clearError,
    submitting,
    message, clearMessage,
    userOptions,
    approvers,
    ticketCategories,
    counts,
    filters,
    updateFilter,
    resetFilter,
    refresh,
    filteredTickets,
    filteredLeaves,
    filteredIntimations,
    filteredAttendance,
    approveItem,
    rejectItem,
    approveTicket,
    reworkTicket,
    moveTicketApproval
  } = useApprovalsData();

  const [actionDialog, setActionDialog] = useState(null);
  const [detailsTicket, setDetailsTicket] = useState(null);
  const [myApprovalsRefreshKey, setMyApprovalsRefreshKey] = useState(0);

  const totalPending = useMemo(
    () => (isTL ? counts.attendance : (counts.tickets + counts.leaves + counts.intimations + counts.attendance)),
    [counts, isTL]
  );

  // If user cannot approve (regular Employee), ALWAYS force 'my-approvals'
  const urlTab = searchParams.get('tab');
  const initialMainTab = !canApprove
    ? 'my-approvals'
    : ((urlTab === 'my-approvals' || urlTab === 'approvals')
        ? urlTab
        : (defaultTab || 'approvals'));

  const [mainTab, setMainTab] = useState(initialMainTab);

  useEffect(() => {
    if (!canApprove) {
      if (mainTab !== 'my-approvals') {
        setMainTab('my-approvals');
      }
      return;
    }
    if (urlTab === 'approvals' || urlTab === 'my-approvals') {
      setMainTab(urlTab);
    }
  }, [urlTab, canApprove, mainTab]);

  const handleMainTabChange = (nextTab) => {
    if (!canApprove && nextTab === 'approvals') return;
    setMainTab(nextTab);
    setSearchParams({ tab: nextTab }, { replace: true });
  };

  const handleRefresh = () => {
    if (canApprove && mainTab === 'approvals') {
      refresh();
    } else {
      setMyApprovalsRefreshKey((k) => k + 1);
    }
  };

  async function handleViewDetails(row) {
    const ticketId = row['Ticket ID'];
    if (!ticketId) return;
    try {
      const response = await fetchTicketDetails(ticketId);
      setDetailsTicket(response?.item || row);
    } catch {
      setDetailsTicket(row);
    }
  }

  async function submitDialogAction(values) {
    let result;
    if (actionDialog.kind === 'ticket-approve') result = await approveTicket(actionDialog.ticketId, values.remarks, values.onBehalfOf);
    if (actionDialog.kind === 'ticket-rework') result = await reworkTicket(actionDialog.ticketId, values.remarks, values.onBehalfOf);
    if (actionDialog.kind === 'ticket-transfer') result = await moveTicketApproval(actionDialog.ticketId, values.target, values.remarks);
    if (actionDialog.kind === 'approve') result = await approveItem(actionDialog.type, actionDialog.id, values);
    if (actionDialog.kind === 'reject') result = await rejectItem(actionDialog.type, actionDialog.id, values);
    if (result?.success !== false) setActionDialog(null);
  }

  const activeUser = currentUser || user;
  const employeeLabel = `${activeUser?.['Employee Name'] || activeUser?.Name || 'User'}${employeeId || activeUser?.['Employee ID'] ? ` | ${employeeId || activeUser?.['Employee ID']}` : ''}`;

  return (
    <section className="page-card approvals-page">
      <div className="page-card__header">
        <div>
          <p className="page-card__eyebrow">
            {canApprove ? 'Approval Center' : 'Request Tracking'}
          </p>
          <h1 className="page-card__title">
            {canApprove
              ? (mainTab === 'approvals' ? 'Pending Approvals' : 'My Approval Status')
              : 'My Approval Status'
            }
          </h1>
        </div>

        <div className="dashboard-controls mobile-header-controls">
          <button
            type="button"
            className="attendance-cta attendance-cta--gray"
            style={{ minHeight: '36px', padding: '0 12px', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            onClick={handleRefresh}
            title="Refresh"
          >
            <RefreshCw size={14} />
            <span>Refresh</span>
          </button>
          <div className="page-card__status">{employeeLabel}</div>
        </div>
      </div>

      {/* Main Switcher: ONLY show if user has canApprove authority (Managers/Admins/etc.) */}
      {canApprove ? (
        <div className="approvals-main-switcher-wrapper">
          <div className="inner-dashboard-tabs approvals-main-switcher">
            <button
              type="button"
              className={`inner-dashboard-tab-btn approvals-main-tab-btn ${mainTab === 'approvals' ? 'inner-dashboard-tab-btn--active' : ''}`}
              onClick={() => handleMainTabChange('approvals')}
            >
              <BadgeCheck size={16} />
              <span>Approval</span>
            </button>

            <button
              type="button"
              className={`inner-dashboard-tab-btn approvals-main-tab-btn ${mainTab === 'my-approvals' ? 'inner-dashboard-tab-btn--active' : ''}`}
              onClick={() => handleMainTabChange('my-approvals')}
            >
              <Receipt size={16} />
              <span>My Approval</span>
            </button>
          </div>
        </div>
      ) : null}

      {/* Tab 1: Approval (Manager / Approver Workflow) - ONLY rendered for approvers */}
      {canApprove ? (
        <div style={{ display: mainTab === 'approvals' ? 'block' : 'none' }}>
          {error ? (
          <div className="dashboard-banner dashboard-banner--error">
            <span>{error}</span>
            <button type="button" className="dashboard-banner__close" onClick={clearError}>OK</button>
          </div>
        ) : null}

        {message ? (
          <div className={`dashboard-banner${message.tone === 'danger' ? ' dashboard-banner--error' : ''}`}>
            <StatusPill tone={message.tone === 'danger' ? 'danger' : 'success'}>{message.tone === 'danger' ? 'Action failed' : 'Action complete'}</StatusPill>
            <span>{message.text}</span>
            <button type="button" className="dashboard-banner__close" onClick={clearMessage}>OK</button>
          </div>
        ) : null}

        <div className="dashboard-kpi-grid approvals-kpi-grid">
          <article className="dashboard-kpi-card">
            <div className="dashboard-kpi-card__icon dashboard-kpi-card__icon--blue">
              <BadgeCheck className="dashboard-kpi-card__icon-svg" />
            </div>
            <p className="dashboard-kpi-card__label">Total Pending</p>
            <p className="dashboard-kpi-card__value">{totalPending}</p>
          </article>
          <article className="dashboard-kpi-card">
            <div className="dashboard-kpi-card__icon dashboard-kpi-card__icon--purple">
              <Tickets className="dashboard-kpi-card__icon-svg" />
            </div>
            <p className="dashboard-kpi-card__label">{isTL ? 'Attendance Queue' : 'Ticket Queue'}</p>
            <p className="dashboard-kpi-card__value">{isTL ? counts.attendance : counts.tickets}</p>
          </article>
          <article className="dashboard-kpi-card">
            <div className="dashboard-kpi-card__icon dashboard-kpi-card__icon--green">
              <Users className="dashboard-kpi-card__icon-svg" />
            </div>
            <p className="dashboard-kpi-card__label">Team Filters</p>
            <p className="dashboard-kpi-card__value">{userOptions.length}</p>
          </article>
        </div>

        <article className="migration-panel migration-panel--full">
          <ApprovalsTabsAndFilters
            activeTab={activeTab}
            counts={counts}
            filters={filters}
            userOptions={userOptions}
            ticketCategories={ticketCategories}
            approvers={approvers}
            currentUser={currentUser}
            onTabChange={setActiveTab}
            onFilterChange={updateFilter}
            onFilterReset={resetFilter}
          />

          {activeTab === 'tickets' ? (
            <TicketApprovalTable
              rows={filteredTickets}
              approvers={approvers}
              submitting={submitting}
              onRequestAction={setActionDialog}
              loading={rowsLoading}
              onViewDetails={handleViewDetails}
            />
          ) : null}

          {activeTab === 'leaves' ? (
            <LeaveApprovalTable rows={filteredLeaves} loading={rowsLoading} submitting={submitting} onRequestAction={setActionDialog} />
          ) : null}

          {activeTab === 'intimations' ? (
            <IntimationApprovalTable rows={filteredIntimations} loading={rowsLoading} submitting={submitting} onRequestAction={setActionDialog} />
          ) : null}

          {activeTab === 'attendance' ? (
            <AttendanceApprovalTable rows={filteredAttendance} loading={rowsLoading} submitting={submitting} onRequestAction={setActionDialog} />
          ) : null}

          <ApprovalServerPager
            loading={rowsLoading}
            pageInfo={pagination[activeTab]}
            onPageChange={(page) => setApprovalPage(activeTab, page)}
          />
        </article>

        {actionDialog ? (
          <ApprovalActionDialog
            action={actionDialog}
            approvers={approvers}
            userOptions={userOptions}
            currentUser={currentUser}
            saving={submitting}
            onClose={() => setActionDialog(null)}
            onSubmit={submitDialogAction}
          />
        ) : null}

        {detailsTicket ? <TicketDetailsDialog ticket={detailsTicket} onClose={() => setDetailsTicket(null)} /> : null}
      </div>
    ) : null}

    {/* Tab 2: My Approval (Personal Request History Workflow) */}
    <div style={{ display: (!canApprove || mainTab === 'my-approvals') ? 'block' : 'none' }}>
      <MyApprovalStatusSection refreshKey={myApprovalsRefreshKey} />
    </div>
    </section>
  );
}
